import type { User } from '../auth/types';
import { isBlockedNavigationFieldName } from './query';
import { isRouteAuthorized } from './routeAuthorization';
import {
  resolveAppBasename,
  resolveRoutePolicy,
  toRouterPath,
} from './routePolicy';
import type { RouterBasename } from './types';

const RETURN_TO_FIELD = 'returnTo';
const VALIDATION_ORIGIN = 'https://academy.invalid';
const INVALID_VISIBLE_LOCATION_CHARACTER = /[\uFFFD\\]/u;
const INVALID_PERCENT_ESCAPE = /%(?![\da-f]{2})/iu;
const TOKEN_ROUTE_PATTERN = /(?:^|\/):token(?:\/|$)/i;

function hasInvalidLocationCharacter(value: string): boolean {
  if (INVALID_VISIBLE_LOCATION_CHARACTER.test(value)) return true;
  return [...value].some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint <= 0x1f || codePoint === 0x7f;
  });
}

function hasInvalidEncodedCharacters(value: string): boolean {
  if (INVALID_PERCENT_ESCAPE.test(value)) return true;
  try {
    return hasInvalidLocationCharacter(decodeURIComponent(value));
  } catch {
    return true;
  }
}

function decodeQueryComponent(value: string): string | null {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return null;
  }
}

function readSingleReturnTo(search: string): string | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  let result: string | null = null;
  let matches = 0;

  for (const pair of query.split('&')) {
    if (!pair) continue;
    const equalsIndex = pair.indexOf('=');
    const encodedName = equalsIndex === -1 ? pair : pair.slice(0, equalsIndex);
    const encodedValue = equalsIndex === -1 ? '' : pair.slice(equalsIndex + 1);
    const name = decodeQueryComponent(encodedName);
    const value = decodeQueryComponent(encodedValue);
    if (name === null || value === null) return null;
    if (name !== RETURN_TO_FIELD) continue;
    matches += 1;
    result = value;
  }

  return matches === 1 && result ? result : null;
}

export function buildLoginTarget(
  loginPath: string,
  requestedDocumentLocation: string,
): string {
  const search = new URLSearchParams({ returnTo: requestedDocumentLocation });
  return `${loginPath}?${search.toString()}`;
}

export function readAuthorizedReturnTo(
  search: string,
  options: { user: User | null; basename: RouterBasename },
): string | null {
  const raw = readSingleReturnTo(search);
  if (!raw
    || !raw.startsWith('/')
    || raw.startsWith('//')
    || hasInvalidLocationCharacter(raw)
    || hasInvalidEncodedCharacters(raw)
    || /^[a-z][a-z\d+.-]*:/i.test(raw)) return null;

  let target: URL;
  try {
    target = new URL(raw, VALIDATION_ORIGIN);
  } catch {
    return null;
  }
  if (target.origin !== VALIDATION_ORIGIN) return null;
  if (`${target.pathname}${target.search}${target.hash}` !== raw) return null;
  if (resolveAppBasename(target.pathname) !== options.basename) return null;
  if ([...target.searchParams.keys()].some(isBlockedNavigationFieldName)) return null;

  const routerPath = toRouterPath(options.basename, target.pathname);
  const policy = resolveRoutePolicy({ basename: options.basename, pathname: routerPath });
  if (!policy || !isRouteAuthorized(policy, options.user)) return null;
  if (TOKEN_ROUTE_PATTERN.test(policy.routerPath)) return null;
  if (policy.shell === 'auth' || policy.shell === 'error' || policy.shell === 'diagnostics') {
    return null;
  }
  return raw;
}
